/**
 * @file useSettingsGroups.ts
 * @description Состояние групп настроек одной панели (phase19.16, SB122;
 * uiux-settings-groups §1–§2; основа SB121).
 *
 *  - от 768 — все открыты, кроме `defaultCollapsed`; свёрнутые помнятся на
 *    устройстве (`commora.settings-groups.<панель>`, см. memory.ts);
 *  - до 768 — открыта одна (последняя открытая или первая), остальные
 *    свёрнуты: список заголовков со сводками и есть оглавление;
 *  - `openGroup(id)` — раскрыть, прокрутить к заголовку под липкую строку,
 *    подсветить 1,5 с (как роль во «Шрифтах») или дать фокус заголовку;
 *  - `?group=<id>` в адресе — тот же `openGroup`, как только группа появилась;
 *    каждый переход (ключ записи истории) — снова, как `?font=` во «Шрифтах»
 *    (общий механизм `useGroupRequestOnce`, ревью партии SB122);
 *  - `focusInvalid` — после прокрутки фокус на первое поле с ошибкой;
 *  - закрыли панель посреди перехода — кадр и прокрутка отменяются.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router";

import type { OpenGroupOptions, SettingsGroupMeta, SettingsGroupsApi } from "./context";
import { expandAllWide, isOpenWide, openNarrowId, readMemory, setOpenWide, writeMemory, type SettingsGroupsMemory } from "./memory";
import { scrollGroupIntoView } from "./scroll";
import { useGroupRequestOnce } from "./useOpenGroupRequest";
import { PHONE_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";

/** Сколько держится подсветка группы после перехода, мс (как у роли во «Шрифтах»). */
export const GROUP_FLASH_MS = 1500;

interface Options {
  panel: string;
  defaultCollapsed?: readonly string[];
}

interface Pending extends Required<OpenGroupOptions> {
  id: string;
  /** Номер запроса: повторный переход к той же группе — снова прокрутка. */
  seq: number;
}

/** Следующий кадр (в окружении без `requestAnimationFrame` — таймер); возвращает отмену. */
function nextFrame(fn: () => void): () => void {
  if (typeof window.requestAnimationFrame === "function") {
    const id = window.requestAnimationFrame(fn);
    return () => window.cancelAnimationFrame(id);
  }
  const timer = window.setTimeout(fn, 0);
  return () => window.clearTimeout(timer);
}

/** Поле с ошибкой: `aria-invalid` у полей ввода, `data-invalid` у строк списков. */
const INVALID_SELECTOR = '[aria-invalid="true"], [data-invalid]';

/** Группы в порядке DOM (регистрируются в порядке эффектов, а условные — позже). */
function byDom(groups: readonly SettingsGroupMeta[]): SettingsGroupMeta[] {
  return [...groups].sort((a, b) => {
    if (!a.el || !b.el || a.el === b.el) return 0;
    return a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
}

export function useSettingsGroups({ panel, defaultCollapsed = [] }: Options): SettingsGroupsApi {
  const narrow = useMediaQuery(PHONE_QUERY);
  const [memory, setMemory] = useState<SettingsGroupsMemory>(() => readMemory(panel));
  const [registered, setRegistered] = useState<SettingsGroupMeta[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [stickyHeight, setStickyHeight] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const scrollingRef = useRef(false);
  const seq = useRef(0);
  // Список по значению: новый массив из пропсов на каждом рендере не должен перезапускать эффекты.
  const defaultsKey = defaultCollapsed.join("\n");
  const defaults = useMemo(() => (defaultsKey ? defaultsKey.split("\n") : []), [defaultsKey]);

  const groups = useMemo(() => byDom(registered), [registered]);
  const ids = useMemo(() => groups.map((g) => g.id), [groups]);
  const narrowOpen = narrow ? openNarrowId(memory, ids, defaults) : null;

  const change = useCallback(
    (fn: (prev: SettingsGroupsMemory) => SettingsGroupsMemory) => {
      setMemory((prev) => {
        const next = fn(prev);
        writeMemory(panel, next);
        return next;
      });
    },
    [panel],
  );

  const isOpen = useCallback(
    (id: string) => (narrow ? id === narrowOpen : isOpenWide(id, memory, defaults)),
    [narrow, narrowOpen, memory, defaults],
  );

  const setOpen = useCallback(
    (id: string, open: boolean) => {
      // До 768 открыта одна: открыть — запомнить её, свернуть — свернуть все.
      if (narrow) change((m) => ({ ...m, last: open ? id : null }));
      else change((m) => setOpenWide(m, id, open, defaults));
    },
    [narrow, change, defaults],
  );

  const openGroup = useCallback(
    (id: string, options: OpenGroupOptions = {}) => {
      setOpen(id, true);
      setActive(id);
      seq.current += 1;
      setPending({ id, focus: options.focus ?? false, flash: options.flash ?? true, focusInvalid: options.focusInvalid ?? false, seq: seq.current });
    },
    [setOpen],
  );

  const expandAll = useCallback(() => change((m) => expandAllWide(m, ids)), [change, ids]);

  const register = useCallback((meta: SettingsGroupMeta) => {
    setRegistered((prev) => [...prev.filter((g) => g.id !== meta.id), meta]);
    return () => setRegistered((prev) => prev.filter((g) => g !== meta));
  }, []);

  // Переход к группе — после отрисовки раскрытой группы (до 768 сворачиваются те, что выше).
  const target = pending ? (groups.find((g) => g.id === pending.id)?.el ?? null) : null;
  const doneSeq = useRef(0);
  // Отмена кадра и прокрутки текущего перехода (при размонтировании).
  const cancelScroll = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      cancelScroll.current?.();
      cancelScroll.current = null;
    },
    [],
  );
  useEffect(() => {
    // Каждый запрос — один раз (повторный рендер с тем же запросом не прокручивает снова).
    if (!pending || !target || doneSeq.current === pending.seq) return;
    doneSeq.current = pending.seq;
    if (pending.flash) setFlashId(pending.id);
    scrollingRef.current = true;
    const trigger = () => target.querySelector<HTMLElement>("[data-settings-group-trigger]");
    cancelScroll.current?.();
    // Кадр — чтобы раскрытая группа успела занять место.
    cancelScroll.current = nextFrame(() => {
      if (!target.isConnected) {
        scrollingRef.current = false;
        return;
      }
      cancelScroll.current = scrollGroupIntoView(target, stickyHeight, () => {
        scrollingRef.current = false;
        cancelScroll.current = null;
        // Поле с ошибкой — после прокрутки к заголовку: фокус сам докрутит до поля, если оно ниже.
        if (pending.focusInvalid) (target.querySelector<HTMLElement>(INVALID_SELECTOR) ?? trigger())?.focus();
      });
      if (pending.focus) trigger()?.focus({ preventScroll: true });
    });
  }, [pending, target, stickyHeight]);

  useEffect(() => {
    if (!flashId) return;
    const timer = window.setTimeout(() => setFlashId(null), GROUP_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashId]);

  // `?group=<id>`: открыть, когда такая группа появилась (SB121 — «где задаётся»);
  // повторная ссылка на ту же группу (новая запись истории) — снова прокрутка.
  const [params] = useSearchParams();
  const { key: locationKey } = useLocation();
  const urlGroup = params.get("group");
  useGroupRequestOnce(urlGroup, urlGroup ? locationKey : null, openGroup, urlGroup !== null && ids.includes(urlGroup));

  return useMemo<SettingsGroupsApi>(
    () => ({
      panel,
      narrow,
      groups,
      isOpen,
      setOpen,
      openGroup,
      expandAll,
      register,
      active,
      setActive,
      flashId,
      scrollingRef,
      rootRef,
      stickyHeight,
      setStickyHeight,
    }),
    [panel, narrow, groups, isOpen, setOpen, openGroup, expandAll, register, active, flashId, stickyHeight],
  );
}
